/**
 * Audit UI Phase 1 — 검색 결과 페이지.
 *
 * URL:
 *   /?audit=results&q=...&scope=...     검색 결과
 *   /?audit=chapter&ch=33              chapter 탐색 (input 없음, chapter 만)
 */
import React, { useMemo, useState, useEffect } from "react";
import SearchBar from "./SearchBar";
import SearchScopeFilter from "./SearchScopeFilter";
import AIVerdictCard from "./AIVerdictCard";
import ResultSection from "./ResultSection";
import PrecedentAuiGrid from "./PrecedentAuiGrid";
import AuditSideNav from "./AuditSideNav";
import { useUnifiedSearch } from "./api";
import SourceEvidenceLink, { tariffEvidenceUrl } from "./SourceEvidenceLink";
import "./theme.css";

function navigate(url) {
  window.history.pushState({}, "", url);
  window.dispatchEvent(new PopStateEvent("popstate"));
}

function readUrl() {
  const p = new URLSearchParams(window.location.search);
  return {
    mode: p.get("audit") || "main",
    q: p.get("q") || "",
    scope: p.get("scope") || "all",
    chapter: p.get("ch") || null,
  };
}

export default function ResultsPage() {
  const url = readUrl();
  const isChapterMode = url.mode === "chapter";
  const query = isChapterMode ? "" : url.q;
  const chapter = url.chapter;

  const search = useUnifiedSearch(query, { chapter });

  // chapter 모드: useUnifiedSearch 가 빈 query 무시 → 직접 호출
  const [chData, setChData] = useState({
    tariff: null,
    precedents: null,
    notes: null,
  });
  useEffect(() => {
    if (!isChapterMode || !chapter) return;
    Promise.all([
      fetch("/api/hs-tariff-search", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ chapter, page_size: 30 }),
      })
        .then((r) => (r.ok ? r.json() : null))
        .catch(() => null),
      fetch("/api/precedents-search", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ chapter, page_size: 2000 }),
      })
        .then((r) => (r.ok ? r.json() : null))
        .catch(() => null),
    ]).then(([tariff, precedents]) => {
      setChData({ tariff, precedents, notes: null });
    });
  }, [isChapterMode, chapter]);

  const tariff = isChapterMode ? chData.tariff : search.tariff;
  const precedents = isChapterMode ? chData.precedents : search.precedents;
  const notes = isChapterMode ? chData.notes : search.notes;
  const ai = isChapterMode ? null : search.ai;
  const loading = isChapterMode ? {} : search.loading;

  // AI 추천 선례 highlight 셋
  const aiPrecedentIds = useMemo(() => {
    const ids = new Set();
    const cases = ai?.audit_evidence?.precedent_cases || [];
    cases.forEach((c) => c.case_id && ids.add(c.case_id));
    return ids;
  }, [ai]);

  // 좌측 scope filter
  const [scope, setScope] = useState(url.scope);
  const showScope = (k) => scope === "all" || scope === k;

  const onSearch = ({ q, scope: s }) => {
    navigate(`/?audit=results&q=${encodeURIComponent(q)}&scope=${s}`);
  };
  const onChapterJump = (ch) => navigate(`/?audit=chapter&ch=${ch}`);
  const onOpenPrecedents = () => navigate("/?audit=precedents");

  const tariffRows = tariff?.rows || [];
  const precRows = precedents?.rows || [];
  const noteRows = notes?.rows || [];

  return (
    <div
      className="audit-root"
      style={{
        minHeight: "100vh",
        background: "var(--audit-bg)",
        display: "flex",
      }}
    >
      <AuditSideNav />
      <div style={{ flex: 1, minWidth: 0 }}>
        <SearchBar
          initialQuery={query}
          initialScope={scope}
          onSearch={onSearch}
          onChapterJump={onChapterJump}
        />

        <div style={{ display: "flex" }}>
          <SearchScopeFilter
            active={scope}
            counts={{
              tariff: tariff?.total,
              precedents: precedents?.total,
              notes: notes?.total,
              laws: null,
            }}
            onSelect={setScope}
          />
          <div
            style={{
              flex: 1,
              padding: "20px 24px",
              maxWidth: 1100,
              overflow: "auto",
            }}
          >
            {isChapterMode && (
              <div
                style={{
                  padding: "8px 12px",
                  marginBottom: 16,
                  background: "var(--audit-accent-bg)",
                  borderRadius: 4,
                  fontSize: 12,
                  color: "var(--audit-text)",
                }}
              >
                Chapter {chapter} 탐색 — 입력 없이 해당 chapter 의 모든 결과
                표시
              </div>
            )}

            {!isChapterMode && (
              <AIVerdictCard
                response={ai}
                line_item={query}
                loading={loading.ai}
                onOpenPrecedents={onOpenPrecedents}
              />
            )}

            {showScope("tariff") && (
              <ResultSection
                title="관세율표"
                count={tariff?.total}
                loading={loading.tariff}
                columns={[
                  { key: "hsk", label: "HSK", mono: true, width: 130 },
                  { key: "popum", label: "품명" },
                  { key: "description_en", label: "Description" },
                  {
                    key: "evidence",
                    label: "근거",
                    width: 52,
                    render: (row) => (
                      <SourceEvidenceLink compact iconOnly url={tariffEvidenceUrl(row.hsk)} label="관세청 원문" />
                    ),
                  },
                ]}
                rows={tariffRows}
                emptyMessage="관세율표 결과 없음"
              />
            )}

            {showScope("precedents") && (
              <PrecedentAuiGrid
                rows={precRows}
                total={precedents?.total}
                loading={loading.precedents}
                highlighted={aiPrecedentIds}
                title="분류사례"
              />
            )}

            {showScope("notes") && (
              <ResultSection
                title="주·해설"
                count={notes?.total}
                loading={loading.notes}
                columns={[
                  { key: "location", label: "위치", width: 220 },
                  { key: "body_excerpt", label: "본문 발췌" },
                ]}
                rows={noteRows.map((r, i) => ({
                  ...r,
                  id: `${r.source_file}-${i}`,
                }))}
                emptyMessage="주·해설 결과 없음 (Phase 2 에서 다른 chapter 데이터 확장)"
              />
            )}

            {showScope("laws") && search.laws && (
              <ResultSection
                title="관련법령"
                count={1}
                loading={loading.laws}
                columns={[
                  { key: "label", label: "구분", width: 140 },
                  { key: "body", label: "본문" },
                ]}
                rows={[
                  {
                    id: "heading",
                    label: `제${search.laws.hs_4digit}호 타이틀`,
                    body: search.laws.heading_title_ko || "(없음)",
                  },
                  ...(search.laws.chapter_notes || [])
                    .slice(0, 3)
                    .map((n, i) => ({
                      id: `cn-${i}`,
                      label: `${search.laws.chapter}류 류주`,
                      body:
                        typeof n === "string"
                          ? n
                          : JSON.stringify(n).slice(0, 200),
                    })),
                  ...(search.laws.section_notes || [])
                    .slice(0, 3)
                    .map((n, i) => ({
                      id: `sn-${i}`,
                      label: `${search.laws.section}부 부주`,
                      body:
                        typeof n === "string"
                          ? n
                          : JSON.stringify(n).slice(0, 200),
                    })),
                ]}
                emptyMessage="관련법령 데이터 없음"
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
